import type { ApiResponse } from './estClient';

import { estClient } from './estClient';

class NotificationApi {
  async getFeed({
    limit,
    beforeId,
  }: { limit?: number; beforeId?: number } = {}): Promise<NotificationFeed> {
    const resp = await estClient.get<NotificationFeedResponse>(
      '/api/notification',
      {
        query: { limit, before_id: beforeId },
      },
    );

    return {
      notifications: resp.notifications,
      unreadCount: resp.unread_count,
    };
  }

  async markRead(ids: number[]): Promise<number> {
    return this.postRead(
      Object.fromEntries(ids.map((id, index) => [`ids[${index}]`, id])),
    );
  }

  async markAllRead(): Promise<number> {
    return this.postRead({ is_all: 1 });
  }

  private async postRead(body: Record<string, number>): Promise<number> {
    const resp = await estClient.post<NotificationReadResponse>(
      '/api/notification/read',
      {
        bodyFormat: 'url-encoded',
        body,
      },
    );

    return resp.unread_count;
  }
}

export const notificationApi = new NotificationApi();

export interface Notification {
  id: number;

  /**
   * What happened - matches NotifyService::TYPE_* on the backend
   */
  type: string;

  title: string | null;
  message: string | null;
  image_url: string | null;

  /**
   * Everything the backend renderers need to word this notification for any channel
   * - the id fields are also what the click on a notification navigates by
   *
   * @see application/EstOffice/Notify/Renderers
   */
  data: {
    leadId?: number | string;
    appealLeadId?: number | string;
    taskLeadId?: number | string;
    taskUnitId?: number | string;

    leadName?: string | null;
    leadPhone?: string | null;
    leadNote?: string | null;

    /** The appeal came together with a brand new lead */
    isNewLead?: boolean;

    /** `appeal_created`, `call_incoming` */
    objectName?: string | null;
    imageUrl?: string;

    /** `appeal_inactive` - whose appeal is hanging, shown to supervisors */
    appealNote?: string | null;
    managerId?: number | string | null;
    managerName?: string | null;

    /** `task_reminder` */
    minutesBefore?: number;
    taskText?: string | null;
    taskComment?: string | null;
    dealId?: number | string | null;
    unitInfo?: string | null;

    /** `test` */
    nonce?: number;
    sentAt?: string;
  };

  /**
   * Null while the notification is unread
   */
  readed_at: string | null;

  created_at: string;
}

export interface NotificationFeed {
  notifications: Notification[];
  unreadCount: number;
}

type NotificationFeedResponse = ApiResponse<{
  notifications: Notification[];
  unread_count: number;
}>;

type NotificationReadResponse = ApiResponse<{ unread_count: number }>;
